import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import Select from "react-select";
import { FaPencilAlt, FaEdit } from "react-icons/fa";
import DummyUser from "../../assets/images/dummy_profile.png";
import { IoMdArrowRoundBack } from "react-icons/io";
import "./Profile.scss";

const API_URL = process.env.REACT_APP_API_URL;
const profileImage = `${process.env.REACT_APP_IMAGE_API_URL}/uploads/profile/`;
const documentImage = `${process.env.REACT_APP_IMAGE_API_URL}/uploads/documents/`;

const Profile = () => {
  const [errors, setErrors] = useState({});
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState("");
  const [error, setError] = useState("");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const validateAadhaar = (aadhaar) => {
    const regex = /^[2-9]{1}[0-9]{11}$/;
    return regex.test(aadhaar);
  };

  const validatePAN = (pan) => {
    const regex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return regex.test(pan);
  };

  const [editForm, setEditForm] = useState({
    username: "",
    email: "",
    whatsapp_number: "",
    state: "",
    city: "",
    area: "",
    dob: "",
    marriage_anniversary_date: "",
    rera_number: "",
    address: "",
    image: null,
    pan_number: "",
    adhar_number: "",
    pincode: "",
    adhar_fornt_image: null,
    adhar_back_image: null,
    pan_card_image: null,
  });

  const getAuthToken = () => localStorage.getItem("token");

  const stateOptions = states.map((state) => ({
    value: state.id,
    label: state.name,
  }));

  const cityOptions = cities.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  useEffect(() => {
    const fetchStates = async () => {
      const token = getAuthToken();
      if (!token) return;
      const res = await fetch(`${API_URL}/state-list`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) setStates(data.data || []);
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      if (!editForm.state) return setCities([]);
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/city-list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ state_id: editForm.state }),
      });
      const data = await res.json();
      if (res.ok) setCities(data.data || []);
    };
    fetchCities();
  }, [editForm.state]);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = getAuthToken();
      if (!token) return setError("User not logged in");
      const res = await fetch(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.status == "1") {
        setProfile(data.data);
        setImageUrl(data.imagePath);
        setEditForm((prev) => ({
          ...prev,
          username: data.data?.username || "",
          email: data.data?.email || "",
          whatsapp_number: data.data?.whatsapp_number || "",
          area: data.data?.area || "",
          dob: data.data?.dob || "",
          marriage_anniversary_date: data.data?.marriage_anniversary_date || "",
          rera_number: data.data?.rera_number || "",
          address: data.data?.address || "",
          pincode: data.data?.pincode || "",
          adhar_number: data.data?.adhar_number || "",
          pan_number: data.data?.pan_number || "",
          state: data.data?.state?.id || "",
          city: data.data?.city?.id || "",
        }));
      } else {
        setError(data.message || "Failed to fetch profile");
      }
      setLoading(false);
    };
    fetchProfile();
  }, []);

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setEditForm({
        ...editForm,
        [fieldName]: file,
      });
    }
  };

  const handleProfileUpdate = async () => {
    const token = getAuthToken();
    if (!token) {
      Swal.fire("Error", "Token not found", "error");
      return;
    }

    const formData = new FormData();

    formData.append("username", editForm.username || "");
    formData.append("email", editForm.email || "");
    formData.append("whatsapp_number", editForm.whatsapp_number || "");
    formData.append("state", editForm.state || "");
    formData.append("city", editForm.city || "");
    formData.append("area", editForm.area || "");
    formData.append("dob", editForm.dob || "");
    formData.append(
      "marriage_anniversary_date",
      editForm.marriage_anniversary_date || ""
    );
    formData.append("rera_number", editForm.rera_number || "");
    formData.append("address", editForm.address || "");
    formData.append("pan_number", editForm.pan_number || "");
    formData.append("adhar_number", editForm.adhar_number || "");
    formData.append("pincode", editForm.pincode || "");

    if (editForm.adhar_fornt_image instanceof File) {
      formData.append("adhar_fornt_image", editForm.adhar_fornt_image);
    }
    if (editForm.adhar_back_image instanceof File) {
      formData.append("adhar_back_image", editForm.adhar_back_image);
    }
    if (editForm.pan_card_image instanceof File) {
      formData.append("pan_card_image", editForm.pan_card_image);
    }

    try {
      const res = await fetch(`${API_URL}/profile-update`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();

      if (res.ok && data.status === "1") {
        Swal.fire(
          "🎉 All Set!",
          "Your profile information is now up-to-date.",
          "success"
        );
        setShowModal(false);
        const refreshRes = await fetch(`${API_URL}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const refreshData = await refreshRes.json();
        if (refreshRes.ok && refreshData.status == "1") {
          setProfile(refreshData.data);
        }
      } else {
        Swal.fire("Error", data.message || "Update failed", "error");
      }
    } catch (error) {
      console.error("Update failed:", error);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const toSentenceCase = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const formatDate = (str) => {
    if (!str) return "";
    const d = new Date(str);
    return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${d.getFullYear()}`;
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  if (loading)
    return <Spinner animation="border" className="mt-5 mx-auto d-block" />;
  if (error) return <p className="text-danger text-center mt-4">{error}</p>;

  return (
    <div className="padding_15">
      <div className="card">
        {/* ═══════════ HEADER — PURPLE GRADIENT ═══════════ */}
        <div className="card-header profile-header-purple">
          <div className="d-flex align-items-center justify-content-between">
            <div className="titlepage">
              <h3>User Profile</h3>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                className="submitbutton_design"
                onClick={() => setShowModal(true)}
              >
                <FaEdit /> Edit Profile
              </button>
              <div className="backbutton">
                <Link to="/dashboard">
                  <IoMdArrowRoundBack /> Back
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row">
            {/* ═══════════ LEFT: PROFILE SUMMARY ═══════════ */}
            <div className="col-md-4 col-12 mb-4">
              <div className="profile-summary">
                <div className="position-relative d-inline-block">
                  {profile && profile.profile ? (
                    <img
                      src={`${profileImage}${profile.profile}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = DummyUser;
                      }}
                      alt="Profile"
                      className="profile_designall"
                    />
                  ) : (
                    <img
                      src={DummyUser}
                      alt="Profile"
                      className="profile_designall"
                    />
                  )}

                  {profile && (
                    <div className="btn_edit alleditbutton">
                      <label
                        htmlFor="fileUpload"
                        className="edit_btn_label d-flex align-items-center gap-1"
                      >
                        <FaPencilAlt className="text-white" />
                      </label>
                      <input
                        type="file"
                        id="fileUpload"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          try {
                            const token = localStorage.getItem("token");
                            const formData = new FormData();
                            formData.append("image", file);
                            const response = await fetch(
                              `${process.env.REACT_APP_API_URL}/profile-image-update`,
                              {
                                method: "POST",
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                },
                                body: formData,
                              }
                            );
                            const data = await response.json();
                            if (response.ok && data.status == "1") {
                              Swal.fire({
                                icon: "success",
                                title: "Image Updated",
                                text: "✅ Profile image updated successfully!",
                                confirmButtonColor: "#226d36",
                              }).then((response) => {
                                if (response.isConfirmed) {
                                  window.location.reload();
                                }
                              });
                            } else {
                              Swal.fire({
                                icon: "warning",
                                title: "Warning",
                                text: "! Internal Server Error",
                                confirmButtonColor: "#90a728ff",
                              });
                            }
                          } catch (err) {
                            console.error("Upload error:", err);
                            Swal.fire({
                              icon: "error",
                              title: "Error Found",
                              text: "❌ Some things went wrongs",
                              confirmButtonColor: "#a72828ff",
                            });
                          }
                        }}
                      />
                    </div>
                  )}
                </div>

                {profile && (
                  <div className="profiledetails">
                    <h5>
                      {profile?.username && toSentenceCase(profile.username)}
                    </h5>
                    <div className="kycstatusdesign">
                      <div className="kycbadge">
                        <label>KYC Status</label>
                        <span
                          className={`kycbadgeall ${
                            toSentenceCase(profile?.kyc).toLowerCase() ===
                            "success"
                              ? "badge-success"
                              : toSentenceCase(profile?.kyc).toLowerCase() ===
                                "pending"
                              ? "badge-warning"
                              : toSentenceCase(profile?.kyc).toLowerCase() ===
                                "rejected"
                              ? "badge-danger"
                              : ""
                          }`}
                        >
                          {toSentenceCase(profile?.kyc)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ═══════════ RIGHT: INFO SECTIONS ═══════════ */}
            <div className="col-md-8 col-12">
              {/* ─────── PERSONAL INFORMATION ─────── */}
              <div className="info-section">
                <h6 className="info-section-title">Personal Information</h6>
                <div className="row">
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">Mobile</span>
                      <span className="info-field-value">
                        {profile?.mobile || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">Email</span>
                      <span className="info-field-value">
                        {profile?.email || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">Designation</span>
                      <span className="info-field-value">
                        {profile?.designation || "NA"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">User Type</span>
                      <span className="info-field-value">
                        {toSentenceCase(profile?.user_type)}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">Date of Birth</span>
                      <span className="info-field-value">
                        {formatDate(profile?.dob) || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">
                        Anniversary Date
                      </span>
                      <span className="info-field-value">
                        {formatDate(profile?.marriage_anniversary_date) ||
                          "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">Parent Name</span>
                      <span className="info-field-value">
                        {profile?.parent_name || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">WhatsApp</span>
                      <span className="info-field-value">
                        {profile?.whatsapp_number || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ─────── ADDRESS INFORMATION ─────── */}
              <div className="info-section">
                <h6 className="info-section-title">Address Information</h6>
                <div className="row">
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">State</span>
                      <span className="info-field-value">
                        {profile?.state?.name || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">City</span>
                      <span className="info-field-value">
                        {profile?.city?.name || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">Area</span>
                      <span className="info-field-value">
                        {toSentenceCase(profile?.area) || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">Pin Code</span>
                      <span className="info-field-value">
                        {profile?.pincode || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="info-field">
                      <span className="info-field-label">Full Address</span>
                      <span className="info-field-value">
                        {toSentenceCase(profile?.address) || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ─────── ACCOUNT & KYC ─────── */}
              <div className="info-section">
                <h6 className="info-section-title">Account & KYC</h6>
                <div className="row">
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">
                        Total Advance Balance
                      </span>
                      <span className="info-field-value">
                        ₹ {profile?.advance_payment ?? "0.00"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">
                        Total Achieved (Buy SQYD)
                      </span>
                      <span className="info-field-value">
                        {profile?.buysqrt ?? "0.00"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">PAN Number</span>
                      <span className="info-field-value">
                        {profile?.pan_number || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">Aadhar Number</span>
                      <span className="info-field-value">
                        {profile?.adhar_number || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">RERA Number</span>
                      <span className="info-field-value">
                        {toSentenceCase(profile?.rera_number) || "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <div className="info-field">
                      <span className="info-field-label">Status</span>
                      <span className="info-field-value">
                        {profile?.rera_number == null ||
                        profile?.rera_number?.trim() === ""
                          ? "Inactive"
                          : "Active"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ─────── DOCUMENT IMAGES ─────── */}
              <div>
                <h6 className="document-section-title">Document Images</h6>
                <div className="row">
                  {/* Aadhar Front */}
                  <div className="col-md-4 col-12 mb-3">
                    <div className="document-card h-100">
                      <div className="card-body text-center">
                        <h6 className="card-title">Aadhar Front</h6>
                        {profile?.adhar_fornt_image ? (
                          <div className="document-preview">
                            <img
                              src={`${profileImage}${profile.adhar_fornt_image}`}
                              alt="Aadhar Front"
                              onClick={() =>
                                openImageModal(
                                  `${profileImage}${profile.adhar_fornt_image}`
                                )
                              }
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        ) : (
                          <div className="text-muted small py-3">
                            <p className="mb-0">No image uploaded</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Aadhar Back */}
                  <div className="col-md-4 col-12 mb-3">
                    <div className="document-card h-100">
                      <div className="card-body text-center">
                        <h6 className="card-title">Aadhar Back</h6>
                        {profile?.adhar_back_image ? (
                          <div className="document-preview">
                            <img
                              src={`${profileImage}${profile.adhar_back_image}`}
                              alt="Aadhar Back"
                              onClick={() =>
                                openImageModal(
                                  `${profileImage}${profile.adhar_back_image}`
                                )
                              }
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        ) : (
                          <div className="text-muted small py-3">
                            <p className="mb-0">No image uploaded</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* PAN Card */}
                  <div className="col-md-4 col-12 mb-3">
                    <div className="document-card h-100">
                      <div className="card-body text-center">
                        <h6 className="card-title">PAN Card</h6>
                        {profile?.pan_card_image ? (
                          <div className="document-preview">
                            <img
                              src={`${profileImage}${profile.pan_card_image}`}
                              alt="PAN Card"
                              onClick={() =>
                                openImageModal(
                                  `${profileImage}${profile.pan_card_image}`
                                )
                              }
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        ) : (
                          <div className="text-muted small py-3">
                            <p className="mb-0">No image uploaded</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ IMAGE PREVIEW MODAL ═══════════ */}
      {showImageModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.8)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Documents Preview</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImageModal(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <img
                  src={selectedImage}
                  alt="Full View"
                  className="img-fluid"
                  style={{ maxHeight: "80vh" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ EDIT PROFILE MODAL ═══════════ */}
      {showModal && (
        <>
          <div className="overlay_design"></div>
          <div className="custom-modal editprofile_modal">
            <div className="modal-content">
              <div className="card">
                <div className="card-header">Edit Profile</div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editForm.username}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              username: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={editForm.email}
                          onChange={(e) =>
                            setEditForm({ ...editForm, email: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">WhatsApp Number</label>
                        <input
                          type="text"
                          maxLength={10}
                          className="form-control"
                          value={editForm.whatsapp_number}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              whatsapp_number: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">Area</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editForm.area}
                          onChange={(e) =>
                            setEditForm({ ...editForm, area: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">Pin Code</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editForm.pincode}
                          placeholder="Enter Pin code"
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              pincode: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">Date of Birth</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="DD-MM-YYYY"
                          value={editForm.dob || ""}
                          onChange={(e) =>
                            setEditForm({ ...editForm, dob: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">
                          Marriage Anniversary Date
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="DD-MM-YYYY"
                          value={editForm.marriage_anniversary_date || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              marriage_anniversary_date: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">State</label>
                        <Select
                          name="state"
                          options={stateOptions}
                          value={stateOptions.find(
                            (opt) => opt.value === editForm.state
                          )}
                          onChange={(selectedOption) =>
                            setEditForm({
                              ...editForm,
                              state: selectedOption?.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">City</label>
                        <Select
                          name="city"
                          options={cityOptions}
                          value={cityOptions.find(
                            (opt) => opt.value === Number(editForm.city)
                          )}
                          onChange={(selectedOption) =>
                            setEditForm({
                              ...editForm,
                              city: selectedOption?.value,
                            })
                          }
                          isDisabled={
                            !editForm.state || cityOptions.length === 0
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">RERA Number</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editForm.rera_number}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              rera_number: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-2">
                        <label className="form-label">
                          Aadhaar Card Number
                        </label>
                        <textarea
                          className={`form-control ${
                            errors.adhar_number ? "is-invalid" : ""
                          }`}
                          value={editForm.adhar_number}
                          maxLength={12}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setEditForm({ ...editForm, adhar_number: value });
                            if (value && !validateAadhaar(value)) {
                              setErrors((prev) => ({
                                ...prev,
                                adhar_number: "Invalid Aadhaar number",
                              }));
                            } else {
                              setErrors((prev) => ({
                                ...prev,
                                adhar_number: "",
                              }));
                            }
                          }}
                        />
                        {errors.adhar_number && (
                          <div className="invalid-feedback">
                            {errors.adhar_number}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-2">
                        <label className="form-label">Pan Card Number</label>
                        <textarea
                          className={`form-control ${
                            errors.pan_number ? "is-invalid" : ""
                          }`}
                          value={editForm.pan_number}
                          maxLength={10}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            setEditForm({ ...editForm, pan_number: value });
                            if (value && !validatePAN(value)) {
                              setErrors((prev) => ({
                                ...prev,
                                pan_number:
                                  "Invalid PAN number (ABCDE1234F)",
                              }));
                            } else {
                              setErrors((prev) => ({
                                ...prev,
                                pan_number: "",
                              }));
                            }
                          }}
                        />
                        {errors.pan_number && (
                          <div className="invalid-feedback">
                            {errors.pan_number}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Aadhar Front Image */}
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">
                          Aadhar Front Image
                        </label>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={(e) =>
                              handleFileChange(e, "adhar_fornt_image")
                            }
                          />
                          {editForm.adhar_fornt_image && (
                            <span className="text-success">✓ Selected</span>
                          )}
                        </div>
                        {editForm.adhar_fornt_image instanceof File ? (
                          <img
                            src={URL.createObjectURL(
                              editForm.adhar_fornt_image
                            )}
                            alt="Aadhar Front Preview"
                            className="img-fluid rounded my-1"
                            style={{
                              minHeight: "120px",
                              maxHeight: "120px",
                              objectFit: "contain",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedImage(
                                URL.createObjectURL(
                                  editForm.adhar_fornt_image
                                )
                              );
                              setShowImageModal(true);
                            }}
                          />
                        ) : profile?.adhar_fornt_image ? (
                          <img
                            src={`${profileImage}${profile.adhar_fornt_image}`}
                            alt="Aadhar Front"
                            className="img-fluid rounded my-1"
                            style={{
                              minHeight: "120px",
                              maxHeight: "120px",
                              objectFit: "contain",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedImage(
                                `${profileImage}${profile.adhar_fornt_image}`
                              );
                              setShowImageModal(true);
                            }}
                          />
                        ) : null}
                      </div>
                    </div>

                    {/* Aadhar Back Image */}
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">
                          Aadhar Back Image
                        </label>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={(e) =>
                              handleFileChange(e, "adhar_back_image")
                            }
                          />
                          {editForm.adhar_back_image && (
                            <span className="text-success">✓ Selected</span>
                          )}
                        </div>
                        {editForm.adhar_back_image instanceof File ? (
                          <img
                            src={URL.createObjectURL(
                              editForm.adhar_back_image
                            )}
                            alt="Aadhar Back Preview"
                            className="img-fluid rounded my-1"
                            style={{
                              minHeight: "120px",
                              maxHeight: "120px",
                              objectFit: "contain",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedImage(
                                URL.createObjectURL(
                                  editForm.adhar_back_image
                                )
                              );
                              setShowImageModal(true);
                            }}
                          />
                        ) : profile?.adhar_back_image ? (
                          <img
                            src={`${profileImage}${profile.adhar_back_image}`}
                            alt="Aadhar Back"
                            className="img-fluid rounded my-1"
                            style={{
                              minHeight: "120px",
                              maxHeight: "120px",
                              objectFit: "contain",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedImage(
                                `${profileImage}${profile.adhar_back_image}`
                              );
                              setShowImageModal(true);
                            }}
                          />
                        ) : null}
                      </div>
                    </div>

                    {/* PAN Card Image */}
                    <div className="col-md-4 col-12">
                      <div className="mb-2">
                        <label className="form-label">PAN Card Image</label>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={(e) =>
                              handleFileChange(e, "pan_card_image")
                            }
                          />
                          {editForm.pan_card_image && (
                            <span className="text-success">✓ Selected</span>
                          )}
                        </div>
                        {editForm.pan_card_image instanceof File ? (
                          <img
                            src={URL.createObjectURL(
                              editForm.pan_card_image
                            )}
                            alt="PAN Card Preview"
                            className="img-fluid rounded my-1"
                            style={{
                              minHeight: "120px",
                              maxHeight: "120px",
                              objectFit: "contain",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedImage(
                                URL.createObjectURL(
                                  editForm.pan_card_image
                                )
                              );
                              setShowImageModal(true);
                            }}
                          />
                        ) : profile?.pan_card_image ? (
                          <img
                            src={`${profileImage}${profile.pan_card_image}`}
                            alt="PAN Card"
                            className="img-fluid rounded my-1"
                            style={{
                              minHeight: "120px",
                              maxHeight: "120px",
                              objectFit: "contain",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedImage(
                                `${profileImage}${profile.pan_card_image}`
                              );
                              setShowImageModal(true);
                            }}
                          />
                        ) : null}
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="mb-2">
                        <label className="form-label">
                          Enter Address Details
                        </label>
                        <textarea
                          className="form-control"
                          value={editForm.address}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              address: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="d-flex justify-content-end gap-2 mt-4">
                        <button
                          className="btn btn-success"
                          onClick={handleProfileUpdate}
                        >
                          &nbsp;&nbsp;Update&nbsp;&nbsp;
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => setShowModal(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Profile;